import { useEffect, useState } from 'react'
import DashboardLayout from './layout'
import MyButton from '../components/buttons'
import { Link } from 'react-router-dom'
import {
  deleteMyAnnounce,
  fetchAnnounceByUserId,
  updateLocalStorageWithUserAnnounce,
} from '../../services/announces'
import { GoAlertFill } from 'react-icons/go'
import { useTranslation } from 'react-i18next'
import { fetchImages } from '../../services/gallery'
import { getUserProfile } from '../../services/profileService'
import { getUser } from '../../services/oneUser'
import GalleryRow from '../components/GalleryRow'
import BoostButton from '../components/BoostButton'
import { FaPen, FaTrash } from 'react-icons/fa'
import Modal from '../components/MyModal'
import VerificationSteps from '../components/VerificationSteps'
import CookieManager from '../lib/CookiesManager'

interface ErrorState {
  type: 'profile' | 'announces' | 'images' | null
  message: string
}

function AnnouncesPage() {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(true)
  const [announces, setAnnounces] = useState<any[]>([])
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [images, setImages] = useState<any[]>([])
  const [hasProfile, setHasProfile] = useState(false)
  const [hasPhone, setHasPhone] = useState(false)
  const [error, setError] = useState<ErrorState>({ type: null, message: '' })
  const excludedFields = [
    'id',
    'userId',
    'createdAt',
    'updatedAt',
    'disponibility',
    'escort',
    'status',
    'practices',
    'boostedTime',
    'timing',
    'price',
    'whatsapp',
    'callOnly',
    'phone',
    'location',
    'smsOnly',
    'boostedAt',
    'emailOnly',
    'isAvailable',
    'timingEscort',
    'priceEscort',
    'views',
    'website',
    'typeEscort',
    'availabilityStart',
    'email',
    'availabilityEnd',
  ]
  const translatableFields = ['location']

  useEffect(() => {
    const checkUserProfile = async () => {
      const userId = CookieManager.getUserId()
      const token = CookieManager.getToken()

      if (!userId || !token) {
        setError({
          type: 'profile',
          message: t('errors.auth-required'),
        })
        setLoading(false)
        return
      }

      try {
        const userProfile = await getUserProfile()
        setHasProfile(!!userProfile)

        const userData = await getUser(userId, token)
        setHasPhone(userData.user?.isPhoneConfirmed === true)

        try {
          const userAnnounces = await fetchAnnounceByUserId(userId, token)

          if (Array.isArray(userAnnounces)) {
            setAnnounces(userAnnounces)
          } else if (userAnnounces && 'id' in userAnnounces) {
            setAnnounces([userAnnounces])
          } else {
            setAnnounces([])
          }
          await updateLocalStorageWithUserAnnounce(userAnnounces)
        } catch (announceError: any) {
          if (announceError?.message === 'Row not found') {
            setAnnounces([])
          } else {
            setError({
              type: 'announces',
              message: t('errors.fetch-announces-failed'),
            })
          }
        }

        try {
          const userImages = await fetchImages(userId, token)
          setImages(userImages)
        } catch (imageError) {
          setError({
            type: 'images',
            message: t('errors.fetch-images-failed'),
          })
        }
      } catch (error) {
        setError({
          type: 'profile',
          message: t('errors.fetch-profile-failed'),
        })
      } finally {
        setLoading(false)
      }
    }

    checkUserProfile()
  }, [t])

  if (loading) {
    ;<DashboardLayout title={t('create-ad.my-announce')}>
      <div className='flex items-center justify-center mt-20 '>
        <div className='animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-redpink'></div>
      </div>
    </DashboardLayout>
  }

  const handleDeleteClick = () => {
    setIsDeleteModalOpen(true)
  }

  const handleDeleteConfirm = async () => {
    const token = CookieManager.getToken()

    if (!token || announces.length === 0 || !announces[0].id) {
      console.error('No announce to delete or missing authentication token.')
      return
    }

    try {
      const announceId = announces[0].id
      await deleteMyAnnounce(announceId, token)
      setAnnounces([])
      localStorage.removeItem('myAnnounce')
    } catch (error) {
      console.error('Error deleting announce:', error)
    } finally {
      setIsDeleteModalOpen(false)
    }
  }

  const boostInfoTooltipContent = t('boost-ad-tooltip')

  return (
    <DashboardLayout title={t('create-ad.my-announce')}>
      <div className='px-0 mx-auto'>
        {error.type && (
          <div className='flex items-center bg-redpink text-white py-2 px-3 rounded-lg text-sm sm:text-base mb-4'>
            <GoAlertFill className='text-xl text-white mr-2 flex-shrink-0' />
            <span>{error.message}</span>
          </div>
        )}
        {!hasProfile || !hasPhone ? (
          <VerificationSteps
            hasPhone={hasPhone}
            hasProfile={hasProfile}
            t={t}
          />
        ) : announces.length > 0 ? (
          <div className='space-y-4'>
            <div className='flex items-center bg-anthracite text-white py-2 px-3 rounded-lg text-sm sm:text-base'>
              <GoAlertFill className='text-xl text-redpink mr-2 flex-shrink-0' />
              <span>{t('create-ad.announce-warning')}</span>
            </div>

            <div className='p-3 sm:p-4 bg-grisclair rounded-lg'>
              <p className='font-bold text-base sm:text-lg mb-1'>
                {t('boost-button-info-title')}
              </p>
              <p className='mb-3 text-sm sm:text-base'>
                {t('boost-button-info')}
              </p>
              <BoostButton
                boostInfoTooltipContent={boostInfoTooltipContent}
                tooltipId='my-tooltip'
              />
            </div>

            <div className='bg-grisclair p-3 sm:p-4 rounded-lg'>
              {announces.map(announce => (
                <div key={announce.id} className='mb-6 last:mb-0'>
                  <div className='grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4'>
                    {Object.entries(announce)
                      .filter(
                        ([key, value]) =>
                          !excludedFields.includes(key) && value,
                      )
                      .map(([key, value]) => (
                        <div
                          key={key}
                          className={`${
                            key === 'title' || key === 'description'
                              ? 'col-span-full'
                              : ''
                          }`}
                        >
                          <div className='font-bold bg-grisclair2 p-2 rounded-lg text-sm sm:text-base mb-2'>
                            {t(`create-ad.${key}`)}
                          </div>
                          <div className='text-sm sm:text-base p-2'>
                            {typeof value === 'object' && value !== null
                              ? Object.entries(value)
                                  .map(([, subValue]) =>
                                    t(`provinces.${subValue}`),
                                  )
                                  .join(', ')
                              : translatableFields.includes(key)
                              ? t(`provinces.${value}`)
                              : (value as string)}
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              ))}

              {images.length > 0 && (
                <div className='mt-4'>
                  <GalleryRow images={images} statusToShow={[1]} />
                </div>
              )}

              <div className='flex flex-col sm:flex-row justify-center items-center gap-3 w-full mt-4'>
                <Link to='/dashboard/announce/' className='w-full sm:w-auto'>
                  <MyButton
                    variant='green'
                    className='flex items-center w-full sm:w-auto'
                  >
                    <FaPen className='mr-2' />
                    <span>{t('create-ad.modify-ad')}</span>
                  </MyButton>
                </Link>

                <MyButton
                  variant='redpink-anthracite'
                  onClick={handleDeleteClick}
                  className='flex items-center w-full sm:w-auto'
                >
                  <FaTrash className='mr-2' />
                  <span>{t('create-ad.delete-ad')}</span>
                </MyButton>
              </div>
            </div>
          </div>
        ) : (
          <div className='space-y-4'>
            <div className='flex items-center bg-anthracite text-white py-2 px-3 rounded-lg text-sm sm:text-base'>
              <GoAlertFill className='text-xl text-redpink mr-2 flex-shrink-0' />
              <span>{t('create-ad.no-announces')}</span>
            </div>

            <Link to='/dashboard/announce/' className='inline-block'>
              <MyButton variant='redpink-anthracite' className='py-3 px-6'>
                {t('create-ad.create-ad')}
              </MyButton>
            </Link>
          </div>
        )}

        <Modal
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
        >
          <div className='bg-anthracite text-white p-4 sm:p-5 font-semibold text-lg sm:text-xl'>
            {t('create-ad.confirm-deletion')}
          </div>

          <div className='p-4 sm:p-6'>
            <GoAlertFill className='text-redpink text-3xl sm:text-4xl mx-auto mb-3' />
            <div
              className='mb-5 text-sm sm:text-base'
              dangerouslySetInnerHTML={{
                __html: t('create-ad.deletion-warning'),
              }}
            />

            <div className='sm:flex-row grid grid-cols-2 justify-end gap-3 sm:gap-4'>
              <MyButton
                variant='anthracite-redpink'
                onClick={() => setIsDeleteModalOpen(false)}
                className='w-full sm:w-auto'
              >
                {t('cancel')}
              </MyButton>
              <MyButton
                variant='redpink-anthracite'
                onClick={handleDeleteConfirm}
                className='w-full sm:w-auto'
              >
                {t('create-ad.delete-ad')}
              </MyButton>
            </div>
          </div>
        </Modal>
      </div>
    </DashboardLayout>
  )
}

export default AnnouncesPage
